/**
 * "Nýtt lykilorð" + "Endurtaktu lykilorðið" with the length counter and the "too common" check. Used by
 * invite / reset links (AcceptTokenScreen) and invite links by voice (SignupScreen).
 */
import { ShieldCheck } from 'lucide-react';
import { useRef, useState, type KeyboardEvent } from 'react';
import { MIN_PASSWORD_LENGTH, passwordProblem } from '../../../shared/kdf';
import { cx } from '../../components/ui';
import { useT } from '../../i18n';
import { PasswordInput } from './PasswordInput';

export interface NewPassword {
  pw: string;
  repeat: string;
}

export const EMPTY_NEW_PASSWORD: NewPassword = { pw: '', repeat: '' };

/** Long enough, not too common, and repeated exactly. */
export function newPasswordOk(v: NewPassword): boolean {
  return !passwordProblem(v.pw) && v.repeat === v.pw;
}

export function NewPasswordFields({
  value,
  onChange,
  tried,
  busy,
}: {
  value: NewPassword;
  onChange: (v: NewPassword) => void;
  /** A submit was attempted: show every problem. */
  tried: boolean;
  busy: boolean;
}) {
  const t = useT();
  const [visible, setVisible] = useState(false);
  const repeatRef = useRef<HTMLInputElement>(null);
  const { pw, repeat } = value;

  const problem = passwordProblem(pw);
  const mismatch = repeat !== pw;
  const pwError = tried && problem ? t(problem === 'too_short' ? 'auth.accept.too_short' : 'auth.accept.too_common', { min: MIN_PASSWORD_LENGTH }) : null;
  // Complain about the repeat only once it is as long as the password (or after a submit attempt).
  const showMismatch = mismatch && (tried || (repeat.length > 0 && repeat.length >= pw.length));
  const repeatError = showMismatch ? t('auth.accept.mismatch') : null;
  const enough = pw.length >= MIN_PASSWORD_LENGTH;

  /**
   * The keyboard's "next" key is a plain Enter, which would submit the form and flag the still
   * empty repeat field. Move to the repeat field instead (submit normally once it has a value).
   */
  const pwKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || e.nativeEvent.isComposing || repeat) return;
    e.preventDefault();
    repeatRef.current?.focus();
  };

  return (
    <>
      <PasswordInput
        label={t('auth.accept.password')}
        name="new-password"
        autoComplete="new-password"
        enterKeyHint="next"
        value={pw}
        onChange={(e) => onChange({ ...value, pw: e.target.value })}
        onKeyDown={pwKeyDown}
        readOnly={busy}
        visible={visible}
        onVisibleChange={setVisible}
        error={pwError}
        hint={
          <>
            {t('auth.accept.rule', { min: MIN_PASSWORD_LENGTH })}{' '}
            <span className={cx('font-semibold whitespace-nowrap tabular-nums', enough ? 'text-[var(--ok-text)]' : 'text-[var(--ink-2)]')}>
              {enough ? <ShieldCheck className="mr-1 inline size-4 align-[-2px]" aria-hidden /> : null}
              {t('auth.accept.count', { count: pw.length, min: MIN_PASSWORD_LENGTH })}
            </span>
          </>
        }
      />
      <PasswordInput
        ref={repeatRef}
        label={t('auth.accept.repeat')}
        name="repeat-password"
        autoComplete="new-password"
        enterKeyHint="done"
        value={repeat}
        onChange={(e) => onChange({ ...value, repeat: e.target.value })}
        readOnly={busy}
        visible={visible}
        onVisibleChange={setVisible}
        noToggle
        error={repeatError}
      />
    </>
  );
}
